import { useState, useRef, useCallback } from 'react';

// Quanti pixel di movimento servono prima di considerare il gesto un trascinamento e non un
// click. Senza soglia, un click per selezionare un nodo lo sposta di un paio di pixel per il
// tremolio della mano, e la formula si sporca senza che te ne accorga.
const SOGLIA_TRASCINAMENTO = 4;

/**
 * Trascinamento dei nodi del canvas.
 *
 * Lo stato del gesto sta in una ref e non in useState: durante il trascinamento arriva un evento
 * per ogni pixel, e tenerlo di stato faceva cambiare identita' a updateDrag a ogni render, con la
 * conseguenza che i listener su document venivano tolti e rimessi in continuazione.
 *
 * @param leggiNodi funzione che restituisce i nodi correnti (una ref, non l'array: cosi' le
 *   callback restano stabili anche mentre i nodi si muovono)
 * @param aggiornaNodi applica in un colpo solo una lista di { id, x, y }
 */
export const useDragAndDrop = (leggiNodi, aggiornaNodi) => {
  // Serve di stato solo perche' il canvas ci attacca i listener e il cursore.
  const [isDragging, setIsDragging] = useState(false);
  const gesto = useRef(null);

  const startDrag = useCallback((nodeId, mousePos, selectedNodes) => {
    const nodi = leggiNodi();
    const nodoTrascinato = nodi.find(n => n.id === nodeId);
    if (!nodoTrascinato) return;

    // Trascinando un nodo che fa parte della selezione si muove tutta la selezione; trascinandone
    // uno fuori selezione si muove solo quello.
    const nelGruppo = selectedNodes.some(n => n.id === nodeId);
    const daTrascinare = nelGruppo && selectedNodes.length > 1
      ? selectedNodes.map(s => nodi.find(n => n.id === s.id)).filter(Boolean)
      : [nodoTrascinato];

    gesto.current = {
      ids: daTrascinare.map(n => n.id),
      posizioniIniziali: Object.fromEntries(daTrascinare.map(n => [n.id, { x: n.x, y: n.y }])),
      mouseIniziale: mousePos,
      superataSoglia: false
    };
    setIsDragging(true);
  }, [leggiNodi]);

  const updateDrag = useCallback((posizioneMouse) => {
    const g = gesto.current;
    if (!g) return;

    const dx = posizioneMouse.x - g.mouseIniziale.x;
    const dy = posizioneMouse.y - g.mouseIniziale.y;

    if (!g.superataSoglia) {
      if (Math.abs(dx) < SOGLIA_TRASCINAMENTO && Math.abs(dy) < SOGLIA_TRASCINAMENTO) return;
      g.superataSoglia = true;
    }

    aggiornaNodi(g.ids.map(id => ({
      id,
      x: g.posizioniIniziali[id].x + dx,
      y: g.posizioniIniziali[id].y + dy
    })));
  }, [aggiornaNodi]);

  const endDrag = useCallback(() => {
    gesto.current = null;
    setIsDragging(false);
  }, []);

  /** True se il gesto in corso ha davvero mosso qualcosa: serve a non trattarlo come un click. */
  const haTrascinato = useCallback(() => gesto.current?.superataSoglia === true, []);

  return { isDragging, startDrag, updateDrag, endDrag, haTrascinato };
};
